import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../lib/cx';
import { Icon } from './Icon';
import './toast.css';

// How long a toast stays, then how long it takes to leave: the --motion-duration of its exit.
const SHOW_MS = 2000;
const LEAVE_MS = 180;

/**
 * Every toast shares one live region at the top of the page, so they stack instead of overlapping.
 * It's made once and left in place, so screen readers are already listening when a toast arrives.
 */
function toastRegion(): HTMLElement {
  const existing = document.querySelector<HTMLElement>('.toast-region');
  if (existing) return existing;
  const region = document.createElement('div');
  region.className = 'toast-region';
  region.setAttribute('role', 'status');
  document.body.append(region);
  return region;
}
const region = toastRegion();

interface ToastProps {
  readonly message: string;
  readonly tone?: 'success' | 'warning' | undefined;
  /** A next step, such as a button; the toast waits while it's pointed at or focused. */
  readonly action?: ReactNode;
  /** Called once the toast has gone back up; key each toast so a new one starts afresh. */
  readonly onDismiss: () => void;
}

/** A brief message that drops in at the top center of the page, then goes back up on its own. */
export function Toast({ message, tone, action, onDismiss }: ToastProps) {
  const [leaving, setLeaving] = useState(false);
  const [held, setHeld] = useState(false);

  // Timers, not animation events, so it still leaves when motion is reduced.
  useEffect(() => {
    if (held) return;
    const leave = setTimeout(() => {
      setLeaving(true);
    }, SHOW_MS);
    const dismiss = setTimeout(onDismiss, SHOW_MS + LEAVE_MS);
    return () => {
      clearTimeout(leave);
      clearTimeout(dismiss);
    };
  }, [held, onDismiss]);

  const hold = (next: boolean) => () => {
    setHeld(next);
    setLeaving(false);
  };

  return createPortal(
    <div
      className={cx('toast', leaving && 'is-leaving')}
      data-tone={tone}
      onPointerEnter={hold(true)}
      onPointerLeave={hold(false)}
      onFocus={hold(true)}
      onBlur={hold(false)}
    >
      {tone && <Icon name={tone === 'success' ? 'check' : 'alert'} />}
      <p className="toast-message">{message}</p>
      {action}
    </div>,
    region,
  );
}
